import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  ShieldAlert, Clock, ClipboardCheck, Scale, Wallet, FileWarning, MessageSquareWarning,
  BarChart3, RefreshCw, CheckCircle2, PauseCircle, RotateCcw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTHZ = 'command_center';
const f$ = (n) => n === null || n === undefined ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const SECTIONS = [
  { id: 'action_required', label: 'Action Required', icon: ShieldAlert, badge: 'action_required' },
  { id: 'confirmation_risk', label: 'Award / Confirmation Risk', icon: Clock, badge: 'confirmation_risk' },
  { id: 'completion_evidence', label: 'Completion Evidence', icon: ClipboardCheck },
  { id: 'reconciliation', label: 'Reconciliation', icon: Scale, badge: 'reconciliation' },
  { id: 'payout_ready', label: 'Payout Ready', icon: Wallet },
  { id: 'compliance', label: 'Compliance', icon: FileWarning, badge: 'compliance' },
  { id: 'disputes', label: 'Disputes', icon: MessageSquareWarning, badge: 'disputes' },
  { id: 'performance', label: 'Partner Performance', icon: BarChart3 },
];

const STATUS_STYLE = {
  READY: 'bg-blue-100 text-blue-800', HOLD_REVIEW: 'bg-orange-100 text-orange-800',
  DISPUTED: 'bg-red-100 text-red-800', APPROVED: 'bg-indigo-100 text-indigo-800',
  SCHEDULED: 'bg-violet-100 text-violet-800', PAID: 'bg-emerald-100 text-emerald-800',
  AWARD_PENDING_CONFIRMATION: 'bg-amber-100 text-amber-800',
  CONFIRMATION_OVERDUE: 'bg-red-100 text-red-800', expiring: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-800',
};

export default function FulfillmentControlTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('action_required');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fulfillment/control/board`);
      if (!r.ok) throw new Error(`Board unavailable (${r.status})`);
      setData(await r.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (key, path, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    try {
      const r = await fetch(`${API}${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `Action failed (${r.status})`);
      toast.success('Done');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const approveRecon = (rec) => act(`ap-${rec}`, `/api/fulfillment/control/reconciliations/${rec}/approve`,
    { authorized_by: AUTHZ }, 'Approve this payout for Finance progression? This is a material decision.');
  const holdRecon = (rec) => { const reason = window.prompt('Hold reason?'); if (reason) act(`hd-${rec}`, `/api/fulfillment/control/reconciliations/${rec}/hold`, { reason }); };
  const markPaid = (rec) => { const ref = window.prompt('Finance / payment reference (required to mark paid):'); if (ref) act(`pd-${rec}`, `/api/fulfillment/control/reconciliations/${rec}/mark-paid`, { finance_ref: ref }, 'Confirm this payout is PAID per Finance evidence? Irreversible.'); };
  const resolveDispute = (id) => act(`ds-${id}`, `/api/fulfillment/control/disputes/${id}/resolve`,
    { authorized_by: AUTHZ, resolution: 'reviewed', restore_status: 'APPROVED' }, 'Resolve this dispute and restore payout to APPROVED?');
  const reviewCompliance = (pid, cmp, status) => act(`cm-${cmp}-${status}`, `/api/fulfillment/control/providers/${pid}/compliance/${cmp}/review`,
    { review_status: status, reviewer: AUTHZ }, status === 'rejected' ? 'Reject this compliance document?' : null);
  const reopen = (rid) => act(`ro-${rid}`, `/api/fulfillment/requests/${rid}/reopen`, { reason: 'ace_control_reopen', by: AUTHZ }, 'Reopen this fulfillment request for re-sourcing?');

  if (loading) return <Card className="p-8 text-center text-sm text-gray-400" data-testid="fulfillment-loading">Loading Fulfillment Control…</Card>;
  if (!data) return <Card className="p-8 text-center text-sm text-gray-400">No data.</Card>;

  const counts = data.counts || {};

  return (
    <div data-testid="fulfillment-control-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 font-bold">Driven Collective</p>
          <h2 className="text-xl font-black tracking-tight">Fulfillment Control</h2>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="fc-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>

      {/* Section nav w/ badges */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200 mb-4">
        {SECTIONS.map((s) => {
          const badge = s.badge ? counts[s.badge] : (data[s.id] ? (Array.isArray(data[s.id]) ? data[s.id].length : Object.keys(data[s.id]).length) : 0);
          return (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`fc-tab-${s.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${section === s.id ? 'border-red-600 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-700'}`}>
              <s.icon className="h-3.5 w-3.5" /> {s.label}
              {badge > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${s.id === 'action_required' || s.badge === 'disputes' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`} data-testid={`fc-badge-${s.id}`}>{badge}</span>}
            </button>
          );
        })}
      </div>

      <div data-testid={`fc-section-${section}`}>
        {['action_required', 'confirmation_risk', 'completion_evidence', 'reconciliation', 'payout_ready'].includes(section) && (
          <RowTable rows={data[section]} section={section} busy={busy}
            approveRecon={approveRecon} holdRecon={holdRecon} markPaid={markPaid} resolveDispute={resolveDispute} reopen={reopen} />
        )}
        {section === 'compliance' && <ComplianceTable rows={data.compliance} busy={busy} review={reviewCompliance} />}
        {section === 'disputes' && <DisputeTable rows={data.disputes} busy={busy} resolveDispute={resolveDispute} />}
        {section === 'performance' && <Performance perf={data.performance} />}
      </div>
    </div>
  );
}

const Empty = ({ text }) => <Card className="p-10 text-center text-sm text-gray-400" data-testid="fc-empty">{text}</Card>;

const RowTable = ({ rows, section, busy, approveRecon, holdRecon, markPaid, resolveDispute, reopen }) => {
  if (!rows?.length) return <Empty text="Nothing needs attention here." />;
  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="text-left px-3 py-2">Provider</th>
            <th className="text-left px-3 py-2">Ref</th>
            <th className="text-left px-3 py-2">Booking</th>
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-right px-3 py-2">Partner Cost</th>
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Required Action</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const rec = r.reconciliation_id;
            const rid = r.fulfillment_request_id;
            const key = rec || r.offer_id || r.dispute_id || i;
            return (
              <tr key={key} className="border-t" data-testid={`fc-row-${key}`}>
                <td className="px-3 py-2 font-semibold">{r.provider_name || r.provider_id}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{rec || r.award_id || r.offer_id || '—'}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{r.booking_id || '—'}</td>
                <td className="px-3 py-2 text-xs">{r.service_date || '—'}{r.confirmation_deadline ? ` · due ${String(r.confirmation_deadline).slice(0, 10)}` : ''}</td>
                <td className="px-3 py-2 text-right">{f$(r.partner_cost ?? r.gross_payable)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[r.status || r.confirmation_status] || 'bg-gray-100 text-gray-700'}`}>
                    {humanize(r.status || r.confirmation_status || '')}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">{r.required_action || (r.hold_review_reason ? `hold: ${r.hold_review_reason}` : '—')}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {rec && r.status === 'READY' && <ActBtn t={`fc-approve-${rec}`} busy={busy === `ap-${rec}`} onClick={() => approveRecon(rec)} icon={CheckCircle2} label="Approve" />}
                  {rec && (r.status === 'READY' || r.status === 'HOLD_REVIEW') && <ActBtn t={`fc-hold-${rec}`} busy={busy === `hd-${rec}`} onClick={() => holdRecon(rec)} icon={PauseCircle} label="Hold" ghost />}
                  {rec && (r.status === 'APPROVED' || r.status === 'SCHEDULED') && <ActBtn t={`fc-markpaid-${rec}`} busy={busy === `pd-${rec}`} onClick={() => markPaid(rec)} icon={Wallet} label="Mark Paid" />}
                  {rec && r.status === 'DISPUTED' && <ActBtn t={`fc-resolve-${rec}`} busy={busy.startsWith('ds-')} onClick={() => resolveDispute(r.dispute_id || rec)} icon={CheckCircle2} label="Resolve" />}
                  {rid && (section === 'confirmation_risk') && <ActBtn t={`fc-reopen-${rid}`} busy={busy === `ro-${rid}`} onClick={() => reopen(rid)} icon={RotateCcw} label="Reopen" ghost />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
};

const ComplianceTable = ({ rows, busy, review }) => {
  if (!rows?.length) return <Empty text="No expiring or expired compliance documents." />;
  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
          <tr><th className="text-left px-3 py-2">Provider</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Capability</th><th className="text-left px-3 py-2">Expiry</th><th className="text-center px-3 py-2">Status</th><th className="text-right px-3 py-2">Actions</th></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.compliance_id} className="border-t" data-testid={`fc-compliance-${c.compliance_id}`}>
              <td className="px-3 py-2 font-semibold">{c.provider_name || c.provider_id}</td>
              <td className="px-3 py-2">{humanize(c.type)}</td>
              <td className="px-3 py-2">{c.capability ? humanize(c.capability) : 'All'}</td>
              <td className="px-3 py-2 text-xs">{c.expiry_date || '—'}</td>
              <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[c.effective_status] || 'bg-gray-100'}`}>{humanize(c.effective_status)}</span></td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <ActBtn t={`fc-compliance-clear-${c.compliance_id}`} busy={busy === `cm-${c.compliance_id}-approved`} onClick={() => review(c.provider_id, c.compliance_id, 'approved')} icon={CheckCircle2} label="Clear" />
                <ActBtn t={`fc-compliance-reject-${c.compliance_id}`} busy={busy === `cm-${c.compliance_id}-rejected`} onClick={() => review(c.provider_id, c.compliance_id, 'rejected')} label="Reject" ghost />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const DisputeTable = ({ rows, busy, resolveDispute }) => {
  if (!rows?.length) return <Empty text="No open disputes." />;
  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
          <tr><th className="text-left px-3 py-2">Provider</th><th className="text-left px-3 py-2">Dispute</th><th className="text-left px-3 py-2">Reconciliation</th><th className="text-left px-3 py-2">Reason</th><th className="text-right px-3 py-2">Actions</th></tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.dispute_id} className="border-t" data-testid={`fc-dispute-${d.dispute_id}`}>
              <td className="px-3 py-2 font-semibold">{d.provider_name || d.provider_id}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{d.dispute_id}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{d.reconciliation_id}</td>
              <td className="px-3 py-2 text-xs text-gray-600">{d.reason}</td>
              <td className="px-3 py-2 text-right"><ActBtn t={`fc-dispute-resolve-${d.dispute_id}`} busy={busy === `ds-${d.dispute_id}`} onClick={() => resolveDispute(d.dispute_id)} icon={CheckCircle2} label="Resolve" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
};

const Performance = ({ perf }) => {
  const entries = Object.entries(perf || {});
  if (!entries.length) return <Empty text="No partner performance data yet." />;
  const pct = (m) => !m || m.value === null || m.value === undefined ? '—' : `${Math.round(m.value * 100)}%`;
  return (
    <div className="space-y-3" data-testid="fc-performance">
      {entries.map(([pid, p]) => (
        <Card key={pid} className="p-4">
          <p className="text-xs font-bold text-gray-700 mb-2">{p.provider_name || pid} <span className="text-gray-400">· {pid} · trailing {p.window_days}d</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <M label="Requests" v={p.requests_received?.value} n={p.requests_received?.sample_size} />
            <M label="Response" v={pct(p.response_rate)} n={p.response_rate?.sample_size} />
            <M label="Acceptance" v={pct(p.acceptance_rate)} n={p.acceptance_rate?.sample_size} />
            <M label="Completion" v={pct(p.completion_rate)} n={p.completion_rate?.sample_size} />
          </div>
        </Card>
      ))}
    </div>
  );
};

const M = ({ label, v, n }) => (
  <div className="bg-gray-50 border rounded p-2"><p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{label}</p><p className="text-lg font-black">{v ?? '—'}</p><p className="text-[10px] text-gray-400">n={n ?? 0}</p></div>
);

const ActBtn = ({ t, busy, onClick, icon: Icon, label, ghost }) => (
  <Button size="sm" variant={ghost ? 'outline' : 'default'} disabled={busy} onClick={onClick} data-testid={t}
    className={`ml-1 h-7 px-2 text-[11px] ${ghost ? '' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
    {Icon && <Icon className="h-3 w-3 mr-1" />}{busy ? '…' : label}
  </Button>
);
